import { Link, usePage } from "@inertiajs/react";
import { BarChart3, Images, Key, LayoutGrid, Sparkles } from "lucide-react";

import { NavMain } from "@/components/nav-main";
import { NavUser } from "@/components/nav-user";
import { Progress } from "@/components/ui/progress";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { analyticsPath, billingPath, dashboardPath, oneOffGenerationsPath } from "@/routes";

import AppLogoIcon from "./app-logo-icon";

const mainNavItems = [
  {
    title: "Dashboard",
    href: dashboardPath(),
    icon: LayoutGrid,
  },
  {
    title: "One-off generations",
    href: oneOffGenerationsPath(),
    icon: Sparkles,
  },
  {
    title: "Analytics",
    href: analyticsPath(),
    icon: BarChart3,
  },
  {
    title: "API Keys",
    href: "/api_keys",
    icon: Key,
  },
];

function formatPlanName(plan?: string | null) {
  if (!plan) return "Starter";
  return plan.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatCount(value: number) {
  return new Intl.NumberFormat("en-US").format(Math.max(0, value));
}

export function AppSidebar() {
  const page = usePage();
  const { flags, plan_quota } = page.props;

  const rawImageLimit = plan_quota?.image_limit ?? 0;
  const hasUnlimitedQuota = plan_quota?.unlimited ?? rawImageLimit < 0;
  const planTotalCredits = hasUnlimitedQuota ? 0 : Math.max(rawImageLimit, 0);
  const planUsedCredits = Math.max(plan_quota?.used ?? 0, 0);
  const planAvailableCredits = hasUnlimitedQuota
    ? null
    : Math.max(plan_quota?.remaining ?? planTotalCredits - planUsedCredits, 0);

  const hasFiniteQuota = planTotalCredits > 0;
  const hasAnyQuota = hasUnlimitedQuota || hasFiniteQuota;
  const isQuotaReached = !hasUnlimitedQuota && hasFiniteQuota && (planAvailableCredits ?? 0) <= 0;

  const usagePercent = hasFiniteQuota ? Math.min((planUsedCredits / planTotalCredits) * 100, 100) : 0;
  const currentPlan = formatPlanName(plan_quota?.plan);

  const filteredMainNavItems = mainNavItems.filter((item) => {
    if (item.title === "One-off generations" && !flags.one_off_generations_feature) {
      return false;
    }
    return true;
  });

  return (
    <Sidebar collapsible="icon" variant="inset">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link href={dashboardPath()} prefetch>
                <div className="flex items-center gap-3">
                  <div className="flex aspect-square size-10 items-center justify-center rounded-xl bg-orange-500">
                    <AppLogoIcon className="size-6 text-white" />
                  </div>
                  <span className="text-2xl font-bold text-orange-600">OG Pilot</span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <NavMain items={filteredMainNavItems} />
      </SidebarContent>

      <SidebarFooter>
        <div className="group-data-[collapsible=icon]:hidden">
          <div className="rounded-lg border border-neutral-200/70 bg-white/70 p-3 shadow-sm dark:border-neutral-700/70 dark:bg-neutral-900/50">
            <div className="mb-3 flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-200">
              <Images className="h-4 w-4 text-amber-600 dark:text-orange-400" />
              <span>Image Usage</span>
            </div>

            {hasUnlimitedQuota ? (
              <div className="text-xs text-neutral-500 dark:text-neutral-400">
                Unlimited generation on{" "}
                <span className="font-medium text-neutral-700 dark:text-neutral-300">{currentPlan}</span> plan.
              </div>
            ) : (
              <div className="space-y-2">
                <Progress value={usagePercent} className="h-2" />
                <div className="flex items-center justify-between text-xs text-neutral-500 dark:text-neutral-400">
                  <span>
                    {formatCount(planUsedCredits)} / {formatCount(planTotalCredits)} used
                  </span>
                  <span>{Math.round(usagePercent)}%</span>
                </div>
              </div>
            )}

            {(!hasAnyQuota || isQuotaReached) && (
              <div className="mt-3">
                <Link
                  href={billingPath()}
                  className="block w-full rounded-md bg-orange-600 px-3 py-1.5 text-center text-xs font-semibold text-white shadow-sm hover:bg-orange-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600"
                >
                  Upgrade Plan
                </Link>
              </div>
            )}
          </div>
        </div>
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  );
}
